/**
 * Bazaarvoice inline rating — wrapper + PLP tile DOM overrides.
 *
 * PDP default/strip styles: `theme/contentful/bazaarvoice-pdp-global.js`
 * (`body.page--product-page`). Tile styles live here so PLP/search work without
 * that body class.
 *
 * Figma PLP tile: desktop 21051:14500 / mobile 21058:55794
 *   - 13px stars, 4px star gap, 8px gap to "(N reviews)"
 *   - 12px / 400 / #282829 — no score, no white box, display-only
 *
 * Beat BV `[data-bv-show] .bv_main_container { background: white !important }`
 * with matching specificity. Avoid `background: '… !important'` shorthand
 * (Chakra expands it incorrectly).
 */

const BV_SHOW_ROOTS = [
  '& [data-bv-show="inline_rating"]',
  '& [data-bv-show="rating_summary"]',
];

/** Build a comma selector under both BV show modes. */
const bv = (suffix = '') => BV_SHOW_ROOTS.map((root) => `${root}${suffix}`).join(', ');

const transparent = {
  backgroundColor: 'transparent !important',
  boxShadow: 'none !important',
};

const noBox = {
  ...transparent,
  margin: '0 !important',
  padding: '0 !important',
};

const inlineRow = {
  ...noBox,
  alignItems: 'center',
  display: 'inline-flex !important',
  flexWrap: 'nowrap',
};

const hide = {
  display: 'none !important',
};

const BazaarvoiceInlineRating = {
  parts: ['container'],
  baseStyle: {
    container: {
      display: 'block',
      width: 'fit-content',
    },
  },
  variants: {
    default: {
      container: {
        minH: '1.5rem',
        mt: 2,
      },
    },
    tile: {
      container: {
        ...transparent,
        alignItems: 'center',
        display: 'inline-flex',
        // Parent `infoSection` already pads the tile copy block.
        minH: '13px',
        mt: 0,
        // Let clicks hit the wrapping product `<Link>` (display-only stars).
        pointerEvents: 'none',
        width: 'fit-content',
        // Empty BV (no reviews / id not in feed) — collapse reserved min-height gap.
        '&[data-bv-empty="true"]': {
          display: 'none !important',
          minHeight: '0 !important',
        },

        [bv()]: {
          ...noBox,
          display: 'inline-flex',
          pointerEvents: 'none',
          width: 'auto',
        },
        [bv(' .bv_inline_rating_div_stars_ratings_and_chevron')]: {
          ...inlineRow,
          gap: '8px',
        },
        [bv(' .bv_main_container')]: {
          ...inlineRow,
          border: 'none !important',
          gap: '8px',
          pointerEvents: 'none',
          width: 'auto',
        },
        [bv(' .bv_rating_summary_buttons_container')]: {
          ...inlineRow,
          gap: '8px !important',
          pointerEvents: 'none',
        },
        [[bv(' .bv_main_rating_button'), bv(' button'), bv(' a')].join(', ')]: {
          ...inlineRow,
          border: 'none !important',
          color: 'inherit !important',
          cursor: 'inherit !important',
          flexShrink: 0,
          gap: '8px !important',
          pointerEvents: 'none !important',
          textDecoration: 'none !important',
          '&:hover, &:focus, &:active': {
            ...transparent,
            border: 'none !important',
          },
        },
        // `rating_summary` → avgRating; `inline_rating` → averageRating + sub_container
        [bv(' .bv_main_container .bv_avgRating_component_container')]: hide,
        [bv(' .bv_main_container .bv_averageRating_component_container')]: hide,
        [bv(' .bv_main_container .bv_sub_container')]: hide,

        [bv(' .bv_main_container .bv_stars_component_container')]: {
          ...inlineRow,
          flexShrink: 0,
          gap: '4px',
          height: '13px !important',
          lineHeight: 0,
        },
        [[bv(' .bv_stars_button_container'), bv(' .bv_stars_svg_no_wrap')].join(', ')]: {
          alignItems: 'center',
          display: 'inline-flex !important',
          flexWrap: 'nowrap',
          gap: '4px !important',
          height: '13px !important',
          lineHeight: 0,
          margin: '0 !important',
          padding: '0 !important',
        },
        [bv(' .bv_stars_component_container svg')]: {
          display: 'block',
          flexShrink: 0,
          height: '13px !important',
          margin: '0 !important',
          maxHeight: '13px !important',
          maxWidth: '13px !important',
          padding: '0 !important',
          width: '13px !important',
        },
        [bv(' .bv_main_container .bv_numReviews_component_container')]: {
          ...noBox,
          display: 'inline-flex !important',
          flexShrink: 0,
        },
        // Native BV count + injected tile count (`sf-bv-tile-review-count`).
        [[
          bv(' .bv_main_container .bv_numReviews_text'),
          bv(' .bv_main_container .sf-bv-tile-review-count'),
        ].join(', ')]: {
          ...transparent,
          color: '#282829 !important',
          display: 'inline !important',
          fontFamily: 'body !important',
          fontSize: '12px !important',
          fontStyle: 'normal !important',
          fontWeight: '400 !important',
          letterSpacing: '0 !important',
          lineHeight: '120% !important',
          margin: '0 !important',
          padding: '0 !important',
          textDecoration: 'none !important',
          textDecorationLine: 'none !important',
          whiteSpace: 'nowrap',
        },
        [[bv(' .bv_offscreen_text'), bv(' .bv-off-screen')].join(', ')]: {
          border: '0',
          clip: 'rect(0 0 0 0)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: '0',
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        },
      },
    },
    strip: {
      container: {
        flexShrink: 0,
        minH: '1rem',
        mt: 0,
      },
    },
  },
  defaultProps: {
    variant: 'default',
  },
};

export default BazaarvoiceInlineRating;
